import localForage from "localforage";
import { pick } from "lodash";
import { v4 } from "uuid";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type UploadQueueStatus = "idle" | "uploading" | "uploaded";

export type UploadQueueItemStatus =
  | "pending"
  | "uploading"
  | "uploaded"
  | "failed_upload";

export type UploadQueueItem = {
  key: string;
  status: UploadQueueItemStatus;
  text: string;
  file?: File;
};

interface DriveUploadQueueStateData {
  itemsInQueue: UploadQueueItem[];
  queueState: UploadQueueStatus;
}

interface DriveUploadQueueState extends DriveUploadQueueStateData {
  setItemsInQueue: (items: UploadQueueItem[]) => void;
  addFilesToQueue: (items: File[]) => void;
  updateItemInQueue: (key: string, updates: Partial<UploadQueueItem>) => void;
  removeItemFromQueue: (key: string) => void;
  reset: () => void;
}

const initialState: DriveUploadQueueStateData = {
  itemsInQueue: [],
  queueState: "idle",
};

const persistedStateProps: (keyof DriveUploadQueueStateData)[] = [];

const getQueueStateFromItems = (items: UploadQueueItem[]) => {
  const hasUploading = items.some((item) => item.status === "uploading");
  if (hasUploading) return "uploading";
  const hasUploaded = items.some((item) => item.status === "uploaded");
  return hasUploaded ? "uploaded" : "idle";
};

export const useDriveUploadQueueStore = create<DriveUploadQueueState>()(
  persist(
    (set) => ({
      ...initialState,
      setItemsInQueue: (items) =>
        set({ itemsInQueue: items, queueState: getQueueStateFromItems(items) }),
      addFilesToQueue: (files) => {
        const items = files.map(
          (file) =>
            ({
              key: v4(),
              status: "pending",
              text: file.name,
              file,
            }) satisfies UploadQueueItem,
        );
        set((state) => {
          const newItems = [...state.itemsInQueue, ...items];
          return {
            itemsInQueue: newItems,
            queueState: getQueueStateFromItems(newItems),
          };
        });
      },
      updateItemInQueue: (key, updates) =>
        set((state) => {
          const items = state.itemsInQueue.map((item) =>
            item.key === key ? { ...item, ...updates } : item,
          );
          return {
            itemsInQueue: items,
            queueState: getQueueStateFromItems(items),
          };
        }),
      removeItemFromQueue: (key) =>
        set((state) => {
          const items = state.itemsInQueue.filter((item) => item.key !== key);
          return {
            itemsInQueue: items,
            queueState: getQueueStateFromItems(items),
          };
        }),
      reset: () =>
        set(() => ({
          ...initialState,
        })),
    }),
    {
      version: 3,
      name: "drive-upload-queue",
      storage: createJSONStorage(() => localForage),
      partialize: (state) => pick(state, persistedStateProps),
    },
  ),
);
